import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { PrefsContext } from '../contexts';
import DeferredComponent from '../ws-scripts/components/DeferredComponent';
import Promo from './Promo/Promo';
import { filterVehiclePromosByYmmt } from '../utilities/promos';
import LoadMoreButton from './LoadMoreButton';
import { aR as ASPECT_RATIO } from '../global-constants';

const Carousel = React.lazy(() => import('./Slick/Carousel'));

const TemplateWrapper = ({
	promos,
	isMobile,
	width,
	isCoupon,
	filterParams,
	csrPlaceholder
}) => {
	const DEFAULT_AUTOPLAY_SPEED = 5000;
	const filteredPromos = filterVehiclePromosByYmmt(promos, filterParams);

	const {
		enableCarousel,
		autoplay,
		aspectRatio,
		autoplaySpeed,
		showLoadMoreButton
	} = useContext(PrefsContext);

	const isTallVerticalCoupon =
		aspectRatio === ASPECT_RATIO.DESKTOP_9X16 ||
		aspectRatio === ASPECT_RATIO.MOBILE_9X16;
	const desktopCards = isTallVerticalCoupon ? 8 : 9;
	const desktopCardsSM = 6;
	const mobileCards = 4;
	const cardsToLoad = isMobile ? mobileCards : desktopCards;

	let renderedSlideshow;
	const isCouponCaro =
		enableCarousel === 'true' &&
		isCoupon &&
		!isMobile &&
		!isTallVerticalCoupon;
	const isTallVerticalCouponCaro =
		enableCarousel === 'true' && isTallVerticalCoupon;

	// This section is a Load More button for the coupon listing placement with carousel false
	const sliceCards = (toSlice) => {
		return filteredPromos.slice(0, toSlice);
	};

	const [showMoreBtn, setShowMoreBtn] = useState(
		showLoadMoreButton === 'true' && filteredPromos.length > cardsToLoad
	);
	const [cards, loadCards] = useState(
		showMoreBtn ? sliceCards(cardsToLoad) : filteredPromos
	);

	useEffect(() => {
		if (!isMobile && showMoreBtn) {
			if (width + 48 < 992) {
				loadCards(sliceCards(desktopCardsSM));
			} else {
				loadCards(sliceCards(desktopCards));
			}
		}
		/* eslint-disable react-hooks/exhaustive-deps */
	}, [width]);

	const carouselComp = (
		<DeferredComponent placeholder={csrPlaceholder}>
			<Carousel
				isMobile={isMobile}
				promos={filteredPromos}
				slickProps={{
					autoplay: autoplay === 'true',
					autoplaySpeed:
						parseInt(autoplaySpeed, 10) || DEFAULT_AUTOPLAY_SPEED
				}}
				width={width}
				isCouponCaro={isCouponCaro || isTallVerticalCouponCaro}
			/>
		</DeferredComponent>
	);

	if (filteredPromos.length === 1) {
		// Display single image without carousel
		const promo = (
			<Promo isMobile={isMobile} isVisible promo={filteredPromos[0]} />
		);

		// Promo may not render (e.g. an incentive promo with no matching incentive)
		if (promo) {
			renderedSlideshow = (
				// Wrap with markup similar to what's added by react-slick
				<div
					className="single-promo-container"
					data-testid="single-container"
				>
					<div className="single-promo">
						<div>{promo}</div>
					</div>
				</div>
			);
		}
	} else if (isCoupon) {
		// For Desktop - render slick when pref is enabled and there's more than 2 promos (screen dragging)
		// For Mobile - render slick when pref is enabled for tall vertical coupons
		// For Tall Vertical Coupons - render slick when pref is enabled and there's more than 2 promos (same as 4x3)
		if (
			(isCouponCaro && filteredPromos.length > 2) ||
			(isTallVerticalCouponCaro &&
				!isMobile &&
				filteredPromos.length > 2) ||
			(isTallVerticalCouponCaro &&
				isMobile &&
				filteredPromos.length > 1) ||
			(enableCarousel === 'true' &&
				isMobile &&
				!isTallVerticalCoupon &&
				filteredPromos.length > 1)
		) {
			renderedSlideshow = carouselComp;
		} else {
			renderedSlideshow = (
				// Wrap with markup similar to what's added by react-slick
				// this is grid
				<div className="coupon-container d-flex flex-wrap">
					{cards?.map((promo) => (
						<Promo
							isMobile={isMobile}
							isVisible
							promo={promo}
							key={promo.id}
						/>
					))}
					<LoadMoreButton
						onClick={() => {
							loadCards(filteredPromos);
							setShowMoreBtn(false);
						}}
						showMoreBtn={showMoreBtn}
					/>
				</div>
			);
		}
	} else {
		renderedSlideshow = carouselComp;
	}

	return renderedSlideshow;
};

TemplateWrapper.propTypes = {
	csrPlaceholder: PropTypes.node,
	promos: PropTypes.arrayOf(PropTypes.shape({})),
	isMobile: PropTypes.bool.isRequired,
	width: PropTypes.number.isRequired,
	filterParams: PropTypes.shape({})
};

export default TemplateWrapper;
